import { useQueries, useQuery } from '@tanstack/react-query'
import { createContext, type ReactNode } from 'react'

import { fetchAreas } from '@/features/areas/api'

import { fetchDcs } from '../../dcs/api/fetch-dcs'
import { fetchTruckClasses, fetchTruckTypes } from '../api'
import { type ConfigEntities } from '../models'
import { configQueryKeys } from '../query-keys'

export type ConfigContextType = ConfigEntities | null

export const ConfigContext = createContext<ConfigContextType>(null)

export function ConfigProvider({
  tenantKey,
  children
}: {
  tenantKey: string
  children: ReactNode
}) {
  const [truckClassesQuery, truckTypesQuery, dcsQuery] = useQueries({
    queries: [
      {
        queryKey: configQueryKeys.truckClasses(),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchTruckClasses(tenantKey, signal),
        retry: false
      },
      {
        queryKey: configQueryKeys.truckTypes(),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchTruckTypes(tenantKey, signal),
        retry: false
      },
      {
        queryKey: configQueryKeys.dcs(),
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          const res = await fetchDcs({ tenantKey }, signal)
          return res.data
        },
        retry: false
      }
    ]
  })

  if (
    truckClassesQuery.isError ||
    truckTypesQuery.isError ||
    dcsQuery.isError
  ) {
    throw truckClassesQuery.error ?? truckTypesQuery.error ?? dcsQuery.error
  }

  const truckClasses = truckClassesQuery.data
  const truckTypes = truckTypesQuery.data
  const dcs = dcsQuery.data

  const areasQuery = useQuery({
    queryKey: configQueryKeys.areas(),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchAreas({ tenantKey }, signal),
    retry: false
  })

  if (areasQuery.isError) {
    throw areasQuery.error
  }

  return (
    <ConfigContext.Provider
      value={{
        truckClasses: truckClasses ?? [],
        truckTypes: truckTypes ?? [],
        dcs: dcs ?? [],
        areas: areasQuery.data ?? []
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}
